import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

/**
 * Garage seller pickup view: opened when garage seller scans customer's QR code.
 * URL: /pickup?t={pickupToken}
 * Only the garage seller who sold the items can see and mark pickup. Sign-in required.
 */
export default function Pickup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn, getToken, isLoaded: authLoaded } = useAuth();
  const token = searchParams.get('t');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  useEffect(() => {
    if (!token || token.trim() === '') {
      setError('Invalid pickup link. Show the QR code from your purchase to the vendor.');
      setLoading(false);
      return;
    }

    if (!authLoaded) {
      setLoading(true);
      return;
    }

    if (!isSignedIn) {
      setLoading(false);
      setError(null);
      setData(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/pickup?t=${encodeURIComponent(token)}`, { getToken });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setError(body.error || `Could not load pickup (${res.status})`);
          setLoading(false);
          return;
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load pickup.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isSignedIn, authLoaded, getToken]);

  const handleMarkPickedUp = async (saleId) => {
    setMarkingId(saleId);
    setError(null);
    try {
      const res = await api('/api/pickup/mark-picked-up', {
        getToken,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ saleId, token }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to mark picked up (${res.status})`);
      }
      const updated = await res.json();
      const nextSales = data.sales.map((s) => (s.id === saleId ? { ...s, status: updated.status } : s));
      setData((prev) => ({ ...prev, sales: nextSales }));
      const allPickedUp = nextSales.every((s) => s.status === 'PickedUp' || s.status === 'picked_up');
      if (allPickedUp) {
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to mark as picked up.');
    } finally {
      setMarkingId(null);
    }
  };

  // Valid token but not signed in — prompt to sign in and scan again (only after auth has loaded)
  if (authLoaded && token && token.trim() !== '' && !isSignedIn) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">Garage seller pickup</h1>
        <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-6">
          <p className="text-amber-900 dark:text-amber-100 font-medium mb-2">Sign in required</p>
          <p className="text-amber-800 dark:text-amber-200 mb-4">
            Only the garage seller who sold the items can confirm pickup. Sign in to your garage seller account, then scan the customer&apos;s QR code again.
          </p>
          <Link
            to="/vendor/login"
            className="inline-block w-full sm:w-auto text-center px-6 py-3 rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Sign in to garage seller account
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-lg mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <p className="text-xl text-slate-500 dark:text-slate-400">Loading pickup…</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">Pickup</h1>
        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  // Guard: never read data.sales when data is null (e.g. wrong vendor 404/403 or state batching)
  if (!data || !data.sales) {
    if (error) {
      return (
        <div className="max-w-lg mx-auto p-6">
          <h1 className="text-2xl font-bold mb-4">Pickup</h1>
          <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      );
    }
    return (
      <div className="max-w-lg mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <p className="text-xl text-slate-500 dark:text-slate-400">Loading pickup…</p>
      </div>
    );
  }

  const pendingCount = data.sales.filter((s) => s.status !== 'PickedUp' && s.status !== 'picked_up').length;

  return (
    <div className="max-w-lg mx-auto p-6 pb-24">
      <h1 className="text-2xl font-bold mb-2">Customer pickup</h1>
      <p className="text-slate-600 dark:text-slate-400 mb-6">
        Mark each item as picked up as the customer takes it.
      </p>

      {error && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 mb-4">
          <p className="text-amber-800 dark:text-amber-200">{error}</p>
        </div>
      )}

      <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          {pendingCount > 0 ? (
            <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm font-medium">
              {pendingCount} pending
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm font-medium">
              All picked up
            </span>
          )}
        </div>
        <ul className="divide-y divide-slate-200 dark:divide-slate-700">
          {data.sales.map((sale) => (
            <li key={sale.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-lg">{sale.itemTitle}</p>
                <p className="text-slate-600 dark:text-slate-400">
                  ${parseFloat(sale.amount).toFixed(2)}
                  {(sale.status === 'PickedUp' || sale.status === 'picked_up') && (
                    <span className="ml-2 px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm">
                      Picked up
                    </span>
                  )}
                </p>
              </div>
              {sale.status !== 'PickedUp' && sale.status !== 'picked_up' && (
                <button
                  type="button"
                  onClick={() => handleMarkPickedUp(sale.id)}
                  disabled={markingId === sale.id}
                  className="shrink-0 h-12 px-6 rounded-xl font-semibold bg-green-600 text-white hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {markingId === sale.id ? 'Marking…' : 'Mark picked up'}
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="p-5 border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex justify-between items-center">
          <span className="text-lg font-semibold text-slate-700 dark:text-slate-300">Order total</span>
          <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">${parseFloat(data.totalAmount).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
